import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../../components/common/CommonTypes';
import { logout } from '../actions';

/**
 * Redux slice state for the current authenticated/identified user.
 */
type SliceState = User;

/**
 * Initial user state.
 */
const initialSiteState: SliceState = {
  instanceId: '',
  token: '',
  tokenExpiresOn: new Date(),
  userId: '',
  dealerCode: '',
  originalSupplyingDealer: '',
  isValid: false,
  error: '',
  recaptchaSiteKey: '',
  isDealer: false,
  loggedIn: false
};

/**
 * User slice containing reducers for setting or clearing user context.
 */
const userSlice = createSlice({
  name: 'user',
  initialState: initialSiteState,
  reducers: {
    /** Replaces the entire user state with the provided payload. */
    setUser: (_, action: PayloadAction<SliceState>) => action.payload,

    /** Resets the user state back to its initial values. */
    resetUser: () => initialSiteState,

    /** Updates the dealer code value on the current user state. */
    setDealerCode: (s, action: PayloadAction<string>) => {
      s.dealerCode = action.payload;
    },
    /** Updates the original supplying dealer on the current user state. */
    setOriginalSupplyingDealer: (s, action: PayloadAction<string>) => {
      s.originalSupplyingDealer = action.payload;
    },
    /** Marks the user as having successfully passed the landing page. */
    setLoggedIn: (s, action: PayloadAction<boolean>) => {
      s.loggedIn = action.payload;
    },
    resetLogoutReason: (s) => {
      s.loggedOutReason = initialSiteState.loggedOutReason;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (s, action) => {
      s.loggedIn = false;
      action.payload?.reason && (s.loggedOutReason = action.payload.reason);
    });
  }
});

export const {
  setUser,
  setDealerCode,
  setOriginalSupplyingDealer,
  resetUser,
  setLoggedIn,
  resetLogoutReason
} = userSlice.actions;

export default userSlice.reducer;
